/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import { jsx } from '@emotion/react'

import {
  EuiButtonEmpty,
  EuiFlexGroup,
  EuiFlexItem,
  EuiFlyout,
  EuiFlyoutBody,
  EuiFlyoutFooter,
  EuiFlyoutHeader,
  EuiHorizontalRule,
  EuiPanel,
  EuiSpacer,
  EuiText,
  EuiTitle,
} from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'

import Status from '../../components/Status'
import BadgeList from '../../components/BadgeList'
import BalanceInfo from '../../components/BalanceInfo'
import DetailsSection from '../../components/DetailsSection'

import type { FunctionComponent } from 'react'
import type { Alert } from './types'

const FIXED_SCOPE = ['Organization']
const FIXED_RECIPIENTS = ['Organization owner', 'Billing contact', 'Operational contact']

type Props = {
  alert: Alert
  onClose: () => void
}

const AlertDetails: FunctionComponent<Props> = ({ alert, onClose }) => {
  const { active, balance, triggers } = alert

  const titleId = 'billing.notifications.prepaid-consumption.alert-details.flyout.title'

  return (
    <EuiFlyout
      data-test-subj='billing.notifications.prepaid-consumption.alert-details.flyout'
      aria-labelledby={titleId}
      ownFocus={true}
      onClose={onClose}
      size='s'
    >
      <EuiFlyoutHeader hasBorder={true}>
        <EuiTitle size='m'>
          <h2 id={titleId}>
            <FormattedMessage
              id='billing.notifications.prepaid-consumption.alert-details.title'
              defaultMessage='Prepaid consumption credit balance alert'
            />
          </h2>
        </EuiTitle>
        <Status active={active} />
      </EuiFlyoutHeader>
      <EuiFlyoutBody>
        <EuiPanel color='subdued' paddingSize='m' hasBorder={false} hasShadow={false}>
          <EuiTitle size='xxs'>
            <h3>
              <FormattedMessage
                id='billing.notifications.prepaid-consumption.alert-details.balance.title'
                defaultMessage='Current vs Credits (ECU)'
              />
            </h3>
          </EuiTitle>
          <EuiSpacer size='xs' />
          <BalanceInfo
            current={Math.max(balance.total - balance.remaining, 0)}
            max={balance.total}
          />
        </EuiPanel>
        <EuiSpacer size='l' />
        <DetailsSection
          title={
            <FormattedMessage
              id='billing.notifications.prepaid-consumption.alert-details.scope.title'
              defaultMessage='Scope'
            />
          }
          helpText={
            <FormattedMessage
              id='billing.notifications.prepaid-consumption.alert-details.scope.help'
              defaultMessage='Prepaid credit consumption balance alerts are scoped to the entire Elastic Cloud organization.'
            />
          }
        >
          <BadgeList items={FIXED_SCOPE} />
        </DetailsSection>

        <EuiHorizontalRule margin='xl' />
        <EuiTitle size='xs'>
          <h3>
            <FormattedMessage
              id='billing.notifications.prepaid-consumption.alert-details.alerts.title'
              defaultMessage='Alerts'
            />
          </h3>
        </EuiTitle>
        <EuiSpacer size='l' />
        <DetailsSection
          title={
            <FormattedMessage
              id='billing.notifications.prepaid-consumption.alert-details.notify-at.title'
              defaultMessage='Notify at'
            />
          }
        >
          <BadgeList items={triggers} />
          <EuiText size='s'>
            <FormattedMessage
              id='billing.notifications.prepaid-consumption.alert-details.notify-at.suffix'
              defaultMessage='remaining credits'
            />
          </EuiText>
        </DetailsSection>
        <EuiSpacer size='l' />
        <DetailsSection
          title={
            <FormattedMessage
              id='billing.notifications.prepaid-consumption.alert-details.recipients.title'
              defaultMessage='Recipients'
            />
          }
          helpText={
            <FormattedMessage
              id='billing.notifications.prepaid-consumption.alert-details.recipients.help'
              defaultMessage='Prepaid consumption credit balance alerts will be sent to these roles in your organization.'
            />
          }
        >
          <BadgeList items={FIXED_RECIPIENTS} />
        </DetailsSection>
      </EuiFlyoutBody>
      <EuiFlyoutFooter>
        <EuiFlexGroup justifyContent='flexEnd'>
          <EuiFlexItem grow={false}>
            <EuiButtonEmpty onClick={onClose}>
              <FormattedMessage
                id='billing.notifications.prepaid-consumption.alert-details.button.close'
                defaultMessage='Close'
              />
            </EuiButtonEmpty>
          </EuiFlexItem>
        </EuiFlexGroup>
      </EuiFlyoutFooter>
    </EuiFlyout>
  )
}

export default AlertDetails
